import { useState, useCallback, useRef, useEffect } from 'react'
import { appsyncQuery } from '../../api/appsync-client.js'
import { useSubscription } from '../../api/useSubscription.js'

const UPDATE_AGENDA = `
  mutation UpdateAgenda($sessionId: ID!, $agenda: AWSJSON!) {
    updateAgenda(sessionId: $sessionId, agenda: $agenda) {
      sessionId updatedAt agenda
    }
  }
`

const UPDATE_REPORT_AGENDA = `
  mutation UpdateReportAgenda($reportId: ID!, $sessionId: ID!, $agenda: AWSJSON!) {
    updateReportAgenda(reportId: $reportId, sessionId: $sessionId, agenda: $agenda) {
      sessionId updatedAt agenda
    }
  }
`

const ON_AGENDA_CHANGED = `
  subscription OnAgendaChanged($sessionId: ID!) {
    onAgendaChanged(sessionId: $sessionId) {
      sessionId updatedAt agenda
    }
  }
`

function generateItemId() {
  return 'ag-' + crypto.randomUUID().slice(0, 8)
}

/**
 * Manages local agenda item state with auto-save.
 *
 * @param {object} params
 * @param {string} params.sessionId
 * @param {string|null} params.reportId  - set when manager is editing a report's session
 * @param {Array} params.initialItems    - initial items array from session.agenda.items
 * @param {boolean} params.locked        - whether the session is locked
 * @param {function} params.onSaved      - called with the full updated session after a successful save
 */
export function useAgenda({ sessionId, reportId, initialItems, locked, onSaved }) {
  const [items, setItems] = useState(initialItems || [])
  const saveTimerRef = useRef(null)
  const isSavingRef = useRef(false)

  // Sync items when parent provides an external update (e.g. session switch)
  const prevInitialRef = useRef(initialItems)
  useEffect(() => {
    if (initialItems !== prevInitialRef.current) {
      prevInitialRef.current = initialItems
      if (!saveTimerRef.current && !isSavingRef.current) {
        setItems(initialItems || [])
      }
    }
  }, [initialItems])

  // Subscribe to agenda changes from the other party via dedicated subscription.
  // Skips when user is actively editing to avoid overwriting in-progress input.
  useSubscription(
    ON_AGENDA_CHANGED,
    { sessionId },
    (data) => {
      const updated = data.onAgendaChanged
      if (!updated) return
      if (saveTimerRef.current || isSavingRef.current) return
      const agenda = typeof updated.agenda === 'string' ? JSON.parse(updated.agenda) : updated.agenda
      const remoteItems = agenda?.items
      if (!remoteItems) return
      setItems((prev) => {
        const prevClean = prev.filter((it) => !it._isNew)
        if (JSON.stringify(prevClean) === JSON.stringify(remoteItems)) return prev
        // Preserve any _isNew items the user is currently adding
        const newItems = prev.filter((it) => it._isNew)
        return [...remoteItems, ...newItems]
      })
      onSaved?.({ sessionId, updatedAt: updated.updatedAt, agenda })
    },
    { enabled: !locked && !!sessionId },
  )

  const saveItems = useCallback(async (newItems) => {
    if (locked) return
    isSavingRef.current = true
    // eslint-disable-next-line no-unused-vars
    const cleanItems = newItems.map(({ _isNew, ...rest }) => rest)
    const agendaJson = JSON.stringify({ items: cleanItems })
    try {
      const result = reportId
        ? await appsyncQuery(UPDATE_REPORT_AGENDA, {
            reportId,
            sessionId,
            agenda: agendaJson,
          }).then((d) => d.updateReportAgenda)
        : await appsyncQuery(UPDATE_AGENDA, {
            sessionId,
            agenda: agendaJson,
          }).then((d) => d.updateAgenda)
      onSaved?.(result)
    } catch (_) {
      // Silently ignore save failures — not ideal but consistent with existing canvas behaviour
    } finally {
      isSavingRef.current = false
    }
  }, [sessionId, reportId, locked, onSaved])

  const saveImmediate = useCallback((newItems) => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
    saveItems(newItems)
  }, [saveItems])

  const saveDebounced = useCallback((newItems) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      saveItems(newItems)
      saveTimerRef.current = null
    }, 800)
  }, [saveItems])

  const addItem = useCallback((currentUserId) => {
    const now = new Date().toISOString()
    const newItem = {
      id: generateItemId(),
      text: '',
      priority: null,
      addedBy: currentUserId,
      checked: false,
      checkedAt: null,
      createdAt: now,
      _isNew: true,
    }
    setItems((prev) => [...prev, newItem])
  }, [])

  const removeItem = useCallback((id) => {
    const newItems = items.filter((it) => it.id !== id)
    setItems(newItems)
    saveImmediate(newItems)
  }, [items, saveImmediate])

  const toggleChecked = useCallback((id) => {
    const now = new Date().toISOString()
    const newItems = items.map((it) =>
      it.id === id
        ? { ...it, checked: !it.checked, checkedAt: !it.checked ? now : null }
        : it
    )
    setItems(newItems)
    saveImmediate(newItems)
  }, [items, saveImmediate])

  const setPriority = useCallback((id, priority) => {
    const newItems = items.map((it) => it.id === id ? { ...it, priority } : it)
    setItems(newItems)
    saveImmediate(newItems)
  }, [items, saveImmediate])

  const setText = useCallback((id, text) => {
    setItems((prev) => {
      const updated = prev.map((it) => it.id === id ? { ...it, text } : it)
      const item = updated.find((it) => it.id === id)
      if (!item?._isNew) saveDebounced(updated)
      return updated
    })
  }, [saveDebounced])

  const commitItem = useCallback((id) => {
    setItems((prev) => {
      const item = prev.find((it) => it.id === id)
      if (!item) return prev
      if (item._isNew && !item.text.trim()) {
        return prev.filter((it) => it.id !== id)
      }
      const committed = prev.map((it) =>
        it.id === id ? { ...it, _isNew: undefined } : it
      )
      saveImmediate(committed)
      return committed
    })
  }, [saveImmediate])

  const importItems = useCallback((newItems) => {
    const now = new Date().toISOString()
    const toAdd = newItems.map((item) => ({
      id: generateItemId(),
      text: item.text,
      priority: item.priority || null,
      addedBy: item.addedBy,
      checked: false,
      checkedAt: null,
      createdAt: now,
    }))
    setItems((prev) => {
      const merged = [...prev, ...toAdd]
      saveImmediate(merged)
      return merged
    })
  }, [saveImmediate])

  const reorderItems = useCallback((fromIdx, toIdx) => {
    if (fromIdx === toIdx) return
    const newItems = [...items]
    const [moved] = newItems.splice(fromIdx, 1)
    newItems.splice(toIdx, 0, moved)
    setItems(newItems)
    saveImmediate(newItems)
  }, [items, saveImmediate])

  return { items, addItem, removeItem, toggleChecked, setPriority, setText, commitItem, reorderItems, importItems }
}
